// src/components/allinone/AllInOne.js
import React, { useState, useEffect, useMemo } from "react";
import {
  getAllReportPdfs,
  getAllReportCallouts,
} from "shared/api/function_data";
import "./AllInOne.css";

const FALLBACK_CALLOUT = "No callout data available for this report.";

/* ── collect every sidebar item whose type === "report" ── */
const extractReportItems = (functionConfig) => {
  if (!functionConfig?.function_sidebar) return [];

  const items = [];
  const sidebar = functionConfig.function_sidebar;

  Object.keys(sidebar).forEach((sectionKey) => {
    const section = sidebar[sectionKey];
    if (!section?.items) return;

    section.items.forEach((item) => {
      if (item.type === "report") {
        items.push({
          key: item.key,
          label: item.label,
          section: section.name,
        });
      }
    });
  });

  return items;
};

/* ═══════════════════════════════════════════
   Single Report Card  (receives data via props)
   ═══════════════════════════════════════════ */
function ReportCard({
  itemKey,
  label,
  week,
  year,
  pdfUrl,
  pdfStatus,
  calloutText,
  calloutStatus,
}) {
  const [calloutExpanded, setCalloutExpanded] = useState(false);

  const isPdfLoading = pdfStatus === "loading";
  const isPdfError = pdfStatus === "error" || pdfStatus === "not_found";
  const isPdfReady = pdfStatus === "success" && pdfUrl;

  const isCalloutLoading = calloutStatus === "loading";
  const isCalloutError = calloutStatus === "error";

  return (
    <div className="aio-report-card" id={`report-${itemKey}`}>
      {/* ── Header ── */}
      <div className="aio-report-header">
        <h3 className="aio-report-title">📊 {label}</h3>
        <span className="aio-report-key">{itemKey}</span>
      </div>

      {/* ── Callout ── */}
      <div className="aio-callout">
        <div
          className="aio-callout-bar"
          onClick={() => setCalloutExpanded((p) => !p)}
        >
          <div className="aio-callout-bar-left">
            <span className="aio-callout-icon">📌</span>
            <span className="aio-callout-label">Callout</span>
            {isCalloutLoading && (
              <span className="aio-callout-badge">Loading…</span>
            )}
          </div>
          <span className="aio-callout-arrow">
            {calloutExpanded ? "▲" : "▼"}
          </span>
        </div>

        {calloutExpanded && (
          <div className="aio-callout-body">
            {isCalloutLoading ? (
              <div className="aio-callout-loader">
                <div className="aio-spinner-sm" />
                <span>Loading callout…</span>
              </div>
            ) : isCalloutError ? (
              <p className="aio-callout-error">
                ⚠️ Failed to load callout data.
              </p>
            ) : (
              <p className="aio-callout-text">
                {calloutText || FALLBACK_CALLOUT}
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── PDF ── */}
      <div className="aio-pdf-wrapper">
        {isPdfLoading ? (
          <div className="aio-pdf-state">
            <div className="aio-spinner" />
            <p>Loading report…</p>
          </div>
        ) : isPdfError ? (
          <div className="aio-pdf-state aio-pdf-state--error">
            <span>⚠️</span>
            <p>
              Unable to load <strong>{label}</strong> for Week {week}, {year}
            </p>
          </div>
        ) : isPdfReady ? (
          <embed
            src={`${pdfUrl}#zoom=page-width&view=FitH`}
            type="application/pdf"
            className="aio-pdf-embed"
          />
        ) : (
          <div className="aio-pdf-state">
            <span>📄</span>
            <p>No PDF available</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════
   AllInOne  –  master page (2 API calls total)
   ═══════════════════════════════════════════ */
export default function AllInOne({
  theme,
  functionId,
  week = "N/A",
  year = "N/A",
  functionConfig,
}) {
  const reportItems = useMemo(
    () => extractReportItems(functionConfig),
    [functionConfig]
  );

  const reportKeys = useMemo(
    () => reportItems.map((item) => item.key),
    [reportItems]
  );

  // ── Batch state: key → { url, status } ──
  const [pdfMap, setPdfMap] = useState({});
  const [calloutMap, setCalloutMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // ── Single batch fetch for ALL pdfs + ALL callouts ──
  useEffect(() => {
    if (
      !functionId ||
      reportKeys.length === 0 ||
      week === "N/A" ||
      year === "N/A"
    ) {
      setPdfMap({});
      setCalloutMap({});
      return;
    }

    const fetchAll = async () => {
      setIsLoading(true);

      // Initialize loading state for every key
      const initPdf = {};
      const initCallout = {};
      reportKeys.forEach((key) => {
        initPdf[key] = { url: null, status: "loading" };
        initCallout[key] = { text: "", status: "loading" };
      });
      setPdfMap(initPdf);
      setCalloutMap(initCallout);

      // Fire both batch requests in parallel
      const [pdfResults, calloutResults] = await Promise.allSettled([
        getAllReportPdfs(functionId, reportKeys, week, year),
        getAllReportCallouts(functionId, reportKeys, week, year),
      ]);

      // ── Process PDF results ──
      const newPdfMap = {};
      if (pdfResults.status === "fulfilled" && pdfResults.value) {
        const data = pdfResults.value;
        reportKeys.forEach((key) => {
          const entry = data[key];
          if (entry && entry.status === "success" && entry.url) {
            newPdfMap[key] = { url: entry.url, status: "success" };
          } else {
            newPdfMap[key] = {
              url: null,
              status: entry?.status || "not_found",
            };
          }
        });
      } else {
        // Entire batch failed
        reportKeys.forEach((key) => {
          newPdfMap[key] = { url: null, status: "error" };
        });
      }
      setPdfMap(newPdfMap);

      // ── Process Callout results ──
      const newCalloutMap = {};
      if (calloutResults.status === "fulfilled" && calloutResults.value) {
        const data = calloutResults.value;
        reportKeys.forEach((key) => {
          const entry = data[key];
          if (entry && entry.status === "success" && entry.callout) {
            newCalloutMap[key] = { text: entry.callout, status: "success" };
          } else {
            newCalloutMap[key] = {
              text: FALLBACK_CALLOUT,
              status: entry?.status || "not_found",
            };
          }
        });
      } else {
        reportKeys.forEach((key) => {
          newCalloutMap[key] = { text: FALLBACK_CALLOUT, status: "error" };
        });
      }
      setCalloutMap(newCalloutMap);

      setIsLoading(false);
    };

    fetchAll();
  }, [functionId, reportKeys.join(","), week, year]);

  return (
    <div className={`aio-container ${theme === "dark" ? "aio-dark" : ""}`}>
      <main className="aio-main">
        <div className="aio-header-banner">
          <div className="aio-header-top">
            <h2 className="aio-page-title">📋 All‑In‑One Report View</h2>
            {isLoading && (
              <span className="aio-loading-pill">
                <span className="aio-spinner-xs" />
                Loading all reports…
              </span>
            )}
          </div>
          <p className="aio-page-sub">
            {functionConfig?.function_details?.full_name ||
              functionConfig?.function_details?.label ||
              functionId}{" "}
            — Week {week}, {year} · {reportItems.length} report
            {reportItems.length !== 1 && "s"}
          </p>
        </div>

        {reportItems.length === 0 ? (
          <div className="aio-empty">
            <span>📭</span>
            <p>No report items found in the sidebar configuration.</p>
          </div>
        ) : (
          <div className="aio-grid">
            {reportItems.map((item) => (
              <ReportCard
                key={item.key}
                itemKey={item.key}
                label={item.label}
                week={week}
                year={year}
                pdfUrl={pdfMap[item.key]?.url || null}
                pdfStatus={pdfMap[item.key]?.status || "loading"}
                calloutText={calloutMap[item.key]?.text || ""}
                calloutStatus={calloutMap[item.key]?.status || "loading"}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}